import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import Select from 'react-select';

import { PartnerCategory } from 'types';
import { State } from 'redux/initialState';

import { getPartnerCategories } from 'modules/partners/redux/partnerCategoryActions';
import { useGetPartnerOptions } from 'modules/_shared/components/Modals/UpdateShares/hooks/useGetPartnerOptions';

import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

// eslint-disable-next-line import/no-unresolved -- SCSS resolved at build
import './SendEmailCustomRecipientsMenu.scss';

type CategoryOption = { value: string; label: string };

type SendEmailCustomRecipientsMenuProps = {
  selectedPartnerOption: CustomAsyncSelectOption | null;
  selectedCategoryOption: CustomAsyncSelectOption | null;
  // eslint-disable-next-line no-unused-vars -- type definition callback param
  onPartnerChange: (value: CustomAsyncSelectOption | null) => void;
  // eslint-disable-next-line no-unused-vars -- type definition callback param
  onCategoryChange: (value: CustomAsyncSelectOption | null) => void;
};

const SendEmailCustomRecipientsMenu: FC<SendEmailCustomRecipientsMenuProps> = ({
  selectedPartnerOption,
  selectedCategoryOption,
  onPartnerChange,
  onCategoryChange,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );
  const partnerCategories = useSelector(
    (state: State) => state.partnerCategories ?? []
  );
  const societyId = actualSociety?._id ?? '';

  const { data: partnerData, actions: partnerActions } = useGetPartnerOptions({
    societyId: societyId ?? '',
  });

  useEffect(() => {
    if (societyId) {
      dispatch(getPartnerCategories(societyId));
    }
  }, [dispatch, societyId]);

  const categoryOptions: CategoryOption[] = (
    partnerCategories as PartnerCategory[]
  ).map((cat) => {
    const count = cat.partnersCount?.total ?? 0;
    return {
      value: cat._id,
      label: `${t(cat.name)} (${count})`,
    };
  });

  const selectedCategoryForSelect = selectedCategoryOption
    ? {
        value: selectedCategoryOption.value,
        label: selectedCategoryOption.label,
      }
    : null;

  const handlePartnerChange = (option: CustomAsyncSelectOption | null) => {
    onPartnerChange(option);
    // Do not call onCategoryChange(null) here: parent clears category in
    // onPartnerChange via setCustomCategoryOption(null). Calling
    // onCategoryChange(null) would make the parent run setTo([]) and wipe
    // the partner email we just set.
  };

  const handleCategoryChange = (option: CategoryOption | null) => {
    if (!option) {
      onCategoryChange(null);
      return;
    }
    onPartnerChange(null);
    onCategoryChange({ value: option.value, label: option.label });
  };

  return (
    <div className="send-email-custom-recipients bg-light w-100">
      <div className="send-email-custom-recipients__row row px-2 pt-2">
        <div className="send-email-custom-recipients__col col-12 col-md-6">
          <div className="send-email-custom-recipients__box border">
            <div className="send-email-custom-recipients__header">
              <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                <input
                  type="radio"
                  className="custom-control-input"
                  name="custom-recipients-type"
                  id="custom-recipients-partner"
                  checked={!!selectedPartnerOption}
                  onChange={() => {
                    onCategoryChange(null);
                  }}
                />
                <label
                  className="custom-control-label"
                  htmlFor="custom-recipients-partner"
                >
                  {t('SelectPartner')}
                </label>
              </div>
            </div>
            <div className="send-email-custom-recipients__input">
              <CustomAsyncSelect
                id="send-email-custom-partner-select"
                value={selectedPartnerOption}
                onChange={handlePartnerChange}
                onFetchData={partnerActions.fetchPartnersByTerm}
                placeholder={t('SearchPartner')}
                minimumInputLength={3}
                defaultOptions={partnerData.partnerOptions}
                noDataLabelKey="NoPartnersHaveBeenFound"
                loadingLabelKey="LoadingPartners"
                errorLabelKey="ErrorLoadingPartners"
                isDisabled={!societyId}
                className="react-select-lg"
              />
            </div>
          </div>
        </div>

        <div className="send-email-custom-recipients__col col-12 col-md-6">
          <div className="send-email-custom-recipients__box border">
            <div className="send-email-custom-recipients__header">
              <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                <input
                  type="radio"
                  className="custom-control-input"
                  name="custom-recipients-type"
                  id="custom-recipients-category"
                  checked={!!selectedCategoryOption}
                  onChange={() => {
                    onPartnerChange(null);
                  }}
                />
                <label
                  className="custom-control-label"
                  htmlFor="custom-recipients-category"
                >
                  {t('SelectCategory')}
                </label>
              </div>
            </div>
            <div className="send-email-custom-recipients__input">
              <Select<CategoryOption>
                id="send-email-custom-category-select"
                value={selectedCategoryForSelect}
                onChange={handleCategoryChange}
                options={categoryOptions}
                placeholder={t('PartnerCategories')}
                isClearable
                isDisabled={!societyId}
                className="react-select react-select-lg"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '42px',
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmailCustomRecipientsMenu;
